import React, { useState, useEffect, useCallback } from 'react';
import { Bike, ShoppingBag, Search, Plus, Minus, Trash2, Send, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORM_META = {
  grab: { name: 'Grab', color: '#00B14F' },
  foodpanda: { name: 'Foodpanda', color: '#D70F64' },
  line_man: { name: 'LINE MAN', color: '#00C300' },
  uber_eats: { name: 'Uber Eats', color: '#000000' },
  deliveroo: { name: 'Deliveroo', color: '#00CCBC' },
  just_eat: { name: 'Just Eat', color: '#FF8000' },
  glovo: { name: 'Glovo', color: '#FFC244' },
  wolt: { name: 'Wolt', color: '#009DE0' },
  bolt_food: { name: 'Bolt Food', color: '#34D186' },
  doordash: { name: 'DoorDash', color: '#FF3008' },
  grubhub: { name: 'Grubhub', color: '#F63440' },
  talabat: { name: 'Talabat', color: '#FF5800' },
  careem: { name: 'Careem', color: '#49B44D' },
  noon_food: { name: 'Noon', color: '#FEEE00' },
  custom_delivery: { name: 'Livraison', color: '#8B5CF6' },
};

export const AdminPosTab = ({ menuItems, categories, tables, formatPrice, fetchData }) => {
  const [cart, setCart] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedCat, setSelectedCat] = useState('all');
  const [orderType, setOrderType] = useState('dine_in');
  const [selectedTable, setSelectedTable] = useState('');
  const [platformId, setPlatformId] = useState('');
  const [platformOrderId, setPlatformOrderId] = useState('');
  const [notes, setNotes] = useState('');
  const [sending, setSending] = useState(false);
  const [deliveryPlatforms, setDeliveryPlatforms] = useState([]);
  const [servers, setServers] = useState([]);
  const [selectedServer, setSelectedServer] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [restRes, srvRes] = await Promise.all([
          axios.get(`${API}/admin/restaurant-settings`),
          axios.get(`${API}/admin/servers`),
        ]);
        const activePlats = restRes.data?.delivery_platforms || [];
        setDeliveryPlatforms(activePlats);
        if (srvRes.data?.length > 0) {
          setServers(srvRes.data);
          setSelectedServer(srvRes.data[0].id);
        }
      } catch { /* ignore */ }
    };
    load();
  }, []);

  const filtered = menuItems.filter(i => {
    const matchSearch = !search || i.name.toLowerCase().includes(search.toLowerCase());
    const matchCat = selectedCat === 'all' || i.category_id === selectedCat || i.category === selectedCat;
    return matchSearch && matchCat && i.is_active !== false;
  });

  const addToCart = (item) => {
    setCart(prev => {
      const existing = prev.find(c => c.item_id === item.id);
      if (existing) return prev.map(c => c.item_id === item.id ? { ...c, quantity: c.quantity + 1 } : c);
      return [...prev, { item_id: item.id, name: item.name, price: item.price, quantity: 1 }];
    });
  };

  const updateQty = (itemId, delta) => {
    setCart(prev => prev.map(c => c.item_id === itemId ? { ...c, quantity: Math.max(0, c.quantity + delta) } : c).filter(c => c.quantity > 0));
  };

  const total = cart.reduce((sum, c) => sum + c.price * c.quantity, 0);

  const placeOrder = async () => {
    if (cart.length === 0) return toast.error('Panier vide');
    if (orderType === 'dine_in' && !selectedTable) return toast.error('Selectionnez une table');
    if (!selectedServer) return toast.error('Aucun serveur disponible');

    setSending(true);
    try {
      const source = orderType === 'platform' ? platformId : orderType;
      await axios.post(`${API}/server/${selectedServer}/orders`, {
        table_id: orderType === 'dine_in' ? selectedTable : `admin-${Date.now()}`,
        items: cart,
        notes,
        server_id: selectedServer,
        server_name: servers.find(s => s.id === selectedServer)?.name || 'Admin',
        is_takeaway: orderType !== 'dine_in',
        order_source: source,
        grab_order_id: orderType === 'platform' ? platformOrderId : '',
      });
      toast.success('Commande envoyee !');
      setCart([]);
      setNotes('');
      setPlatformOrderId('');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grid grid-cols-12 gap-4" data-testid="admin-pos-tab">
      {/* Left: Menu */}
      <div className="col-span-8">
        {/* Order type selector */}
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <button onClick={() => setOrderType('dine_in')}
            className={`px-3 py-2 rounded-xl text-xs font-bold transition-all ${orderType === 'dine_in' ? 'bg-[#0A422D] text-white' : 'bg-white text-[#2D241D] border border-[#E5DCD3]'}`}
            data-testid="pos-type-dine-in">Sur place</button>
          <button onClick={() => setOrderType('takeaway')}
            className={`px-3 py-2 rounded-xl text-xs font-bold transition-all ${orderType === 'takeaway' ? 'bg-[#D4A853] text-black' : 'bg-white text-[#2D241D] border border-[#E5DCD3]'}`}
            data-testid="pos-type-takeaway">
            <ShoppingBag className="w-3 h-3 inline mr-1" />Emporter</button>
          {deliveryPlatforms.map(p => {
            const pid = typeof p === 'string' ? p : p.id;
            const meta = PLATFORM_META[pid] || { name: p.name || pid, color: '#888' };
            return (
              <button key={pid} onClick={() => { setOrderType('platform'); setPlatformId(pid); }}
                className={`px-3 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1 ${
                  orderType === 'platform' && platformId === pid
                    ? 'text-white' : 'bg-white text-[#2D241D] border border-[#E5DCD3]'
                }`}
                style={orderType === 'platform' && platformId === pid ? { backgroundColor: meta.color } : {}}
                data-testid={`pos-type-${pid}`}>
                <Bike className="w-3 h-3" />{meta.name}</button>
            );
          })}
        </div>

        {/* Table / Platform selector */}
        <div className="flex items-center gap-2 mb-3">
          {orderType === 'dine_in' && (
            <select value={selectedTable} onChange={e => setSelectedTable(e.target.value)}
              className="h-9 px-3 rounded-xl border border-[#E5DCD3] text-sm bg-white text-[#2D241D]"
              data-testid="pos-table-select">
              <option value="">-- Table --</option>
              {tables.map(t => <option key={t.id} value={t.id}>Table {t.number || t.id}</option>)}
            </select>
          )}
          {orderType === 'platform' && (
            <Input value={platformOrderId} onChange={e => setPlatformOrderId(e.target.value)}
              placeholder={`N° commande ${PLATFORM_META[platformId]?.name || ''}`}
              className="h-9 text-sm rounded-xl border-[#E5DCD3] max-w-[250px]"
              data-testid="pos-platform-order-id" />
          )}
          <select value={selectedServer} onChange={e => setSelectedServer(e.target.value)}
            className="h-9 px-3 rounded-xl border border-[#E5DCD3] text-sm bg-white text-[#2D241D]"
            data-testid="pos-server-select">
            {servers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>

        {/* Search + Categories */}
        <div className="flex items-center gap-2 mb-3">
          <div className="relative flex-1 max-w-[250px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6E5E52]" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..."
              className="pl-8 h-9 rounded-xl border-[#E5DCD3] text-sm" data-testid="pos-search" />
          </div>
          <div className="flex gap-1 overflow-x-auto no-scrollbar">
            <button onClick={() => setSelectedCat('all')}
              className={`px-2.5 py-1.5 rounded-lg text-[11px] font-medium flex-shrink-0 ${selectedCat === 'all' ? 'bg-[#0A422D] text-white' : 'bg-[#F3EDE5] text-[#6E5E52]'}`}>Tout</button>
            {categories.map(c => (
              <button key={c.id} onClick={() => setSelectedCat(c.id)}
                className={`px-2.5 py-1.5 rounded-lg text-[11px] font-medium flex-shrink-0 ${selectedCat === c.id ? 'bg-[#0A422D] text-white' : 'bg-[#F3EDE5] text-[#6E5E52]'}`}>{c.name_fr || c.name}</button>
            ))}
          </div>
        </div>

        {/* Items Grid */}
        <div className="grid grid-cols-4 sm:grid-cols-5 lg:grid-cols-6 gap-2 max-h-[60vh] overflow-y-auto">
          {filtered.map(item => (
            <button key={item.id} onClick={() => addToCart(item)}
              className="bg-white rounded-xl p-2 border border-[#E5DCD3] hover:border-[#0A422D] hover:shadow-md transition-all text-left active:scale-95"
              data-testid={`pos-item-${item.id}`}>
              <div className="w-full aspect-square mb-1 rounded-lg bg-[#F3EDE5] flex items-center justify-center overflow-hidden"
                style={item.image_url ? { backgroundImage: `url(${item.image_url})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}>
                {!item.image_url && <span className="text-sm font-bold text-[#D4A853]">{item.name?.charAt(0)}</span>}
              </div>
              <p className="text-[11px] font-medium text-[#2D241D] line-clamp-2 leading-tight">
                {item.name} {item.spice_level > 0 && <span>{'🌶'.repeat(item.spice_level)}</span>}
              </p>
              <p className="text-[11px] font-bold text-[#0A422D] mt-0.5">{formatPrice(item.price)}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Right: Cart */}
      <div className="col-span-4">
        <div className="bg-white rounded-2xl border border-[#E5DCD3] p-4 sticky top-4" data-testid="pos-cart">
          <h3 className="font-bold text-[#2D241D] text-sm mb-3 flex items-center justify-between">
            Panier ({cart.length})
            {cart.length > 0 && (
              <button onClick={() => setCart([])} className="text-[10px] text-red-400 hover:text-red-600">Vider</button>
            )}
          </h3>

          {cart.length === 0 ? (
            <p className="text-xs text-[#6E5E52] text-center py-8">Ajoutez des articles</p>
          ) : (
            <div className="space-y-2 max-h-[40vh] overflow-y-auto mb-3">
              {cart.map(item => (
                <div key={item.item_id} className="flex items-center gap-2 bg-[#FAF7F2] rounded-xl p-2" data-testid={`cart-item-${item.item_id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-[#2D241D] line-clamp-1">{item.name}</p>
                    <p className="text-[10px] text-[#0A422D] font-bold">{formatPrice(item.price)}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => updateQty(item.item_id, -1)} className="w-6 h-6 rounded-md bg-white border border-[#E5DCD3] flex items-center justify-center">
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="w-6 text-center text-xs font-bold">{item.quantity}</span>
                    <button onClick={() => updateQty(item.item_id, 1)} className="w-6 h-6 rounded-md bg-white border border-[#E5DCD3] flex items-center justify-center">
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes..."
            className="h-8 text-xs rounded-lg border-[#E5DCD3] mb-3" data-testid="pos-notes" />

          <div className="flex items-center justify-between mb-3 pt-2 border-t border-[#E5DCD3]">
            <span className="text-sm font-bold text-[#2D241D]">Total</span>
            <span className="text-lg font-black text-[#0A422D]" data-testid="pos-total">{formatPrice(total)}</span>
          </div>

          <Button onClick={placeOrder} disabled={sending || cart.length === 0}
            className="w-full h-10 bg-[#0A422D] hover:bg-[#083323] text-white font-bold rounded-xl"
            data-testid="pos-send-order">
            {sending ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : (
              <><Send className="w-4 h-4 mr-2" /> Envoyer en cuisine</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
